import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Switch } from "../components/ui/switch";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { 
  ArrowLeft, Camera, Plus, Trash2, Save, LogOut, MapPin, Clock, GlassWater, UserX, Shield, FileText, ScrollText, ShieldCheck, Key, Eye, EyeOff, Check, X, CalendarIcon, Link as LinkIcon, ChevronDown, Search, Bell, Mail
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle 
} from "../components/ui/alert-dialog";
import { Capacitor } from '@capacitor/core';
import { format } from "date-fns";
import AIProfileAssistant from "../components/AIProfileAssistant";

// Time options for dropdowns
const timeOptions = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, '0');
    const min = m.toString().padStart(2, '0');
    const time24 = `${hour}:${min}`;
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? 'AM' : 'PM';
    const label = `${hour12}:${min.toString().padStart(2, '0')} ${ampm}`;
    timeOptions.push({ value: time24, label });
  }
}

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, token, updateUser, logout } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [locationSearchMode, setLocationSearchMode] = useState({}); // Track search vs manual mode per location
  const [searchInputs, setSearchInputs] = useState({}); // Track search input values
  
  // Form state
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [venmoLink, setVenmoLink] = useState(user?.venmo_link || "");
  const [cashappLink, setCashappLink] = useState(user?.cashapp_link || "");
  const [paypalLink, setPaypalLink] = useState(user?.paypal_link || "");
  const [locations, setLocations] = useState(user?.work_locations || []);
  const [requireFollowApproval, setRequireFollowApproval] = useState(user?.require_follow_approval || false);
  const [followersVisibility, setFollowersVisibility] = useState(user?.followers_visibility || "everyone");
  const [vendorNotificationsEnabled, setVendorNotificationsEnabled] = useState(user?.notification_preferences?.vendor_notifications !== false);
  const [appTipsEnabled, setAppTipsEnabled] = useState(user?.notification_preferences?.app_tips !== false);
  
  // Home location state
  const [homeLocation, setHomeLocation] = useState(user?.home_location || null);
  const [homeLocationSearch, setHomeLocationSearch] = useState(user?.home_location?.address || "");
  const [homeAutocompleteInitialized, setHomeAutocompleteInitialized] = useState(false);

  const isBartender = user?.role === "bartender";
  const isNativePlatform = Capacitor.isNativePlatform();

  // Convert base64 to Blob for upload
  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  // Native camera/photo picker using Capacitor Camera plugin with safe dynamic import
  const handleNativeImagePick = async () => {
    try {
      // Dynamically import to avoid crashes if plugin not properly installed
      const { Camera: CapacitorCamera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      
      // Request permissions first
      const permissions = await CapacitorCamera.checkPermissions();
      if (permissions.photos === 'denied' || permissions.camera === 'denied') {
        const requested = await CapacitorCamera.requestPermissions();
        if (requested.photos === 'denied' && requested.camera === 'denied') {
          toast.error("Camera and photo library access is required to upload a profile picture");
          return;
        }
      }

      // Show action sheet to choose camera or gallery
      const image = await CapacitorCamera.getPhoto({
        quality: 80,
        allowEditing: true,
        resultType: CameraResultType.Base64,
        source: CameraSource.Prompt, // Shows action sheet: Camera, Photos, or Cancel
        width: 500,
        height: 500,
        correctOrientation: true,
        presentationStyle: 'popover', // Important for iPad - prevents crash
      });

      if (!image.base64String) {
        return;
      }

      setUploading(true);

      // Convert base64 to blob
      const mimeType = `image/${image.format || 'jpeg'}`;
      const blob = base64ToBlob(image.base64String, mimeType);
      const file = new File([blob], `profile.${image.format || 'jpg'}`, { type: mimeType });

      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/profile/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      updateUser({ profile_image: response.data.path });
      toast.success("Profile picture updated!");
    } catch (e) {
      // User cancelled - don't show error
      if (e.message?.includes('cancelled') || e.message?.includes('User cancelled')) {
        return;
      }
      console.error("Image pick error:", e);
      // Fall back to file input on any error
      toast.error("Camera not available. Please use the file picker.");
      fileInputRef.current?.click();
    } finally {
      setUploading(false);
    }
  };

  // Web file input handler (fallback for browsers)
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/profile/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      updateUser({ profile_image: response.data.path });
      toast.success("Profile picture updated!");
    } catch (e) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  // Unified image picker - uses native on iOS/Android, file input on web
  const handleImagePick = () => {
    if (isNativePlatform) {
      handleNativeImagePick();
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const endpoint = isBartender ? "/profile/bartender" : "/profile/customer";
      const notificationPrefs = {
        vendor_notifications: vendorNotificationsEnabled,
        app_tips: appTipsEnabled
      };
      const data = isBartender 
        ? { name, bio, venmo_link: venmoLink, cashapp_link: cashappLink, paypal_link: paypalLink, work_locations: locations, require_follow_approval: requireFollowApproval, followers_visibility: followersVisibility, notification_preferences: notificationPrefs, home_location: homeLocation }
        : { name, bio, require_follow_approval: requireFollowApproval, followers_visibility: followersVisibility, notification_preferences: notificationPrefs, home_location: homeLocation };
      
      const response = await axios.put(`${API}${endpoint}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser(response.data);
      toast.success("Profile updated!");
      navigate(-1);
    } catch (e) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  // Home location autocomplete
  // Initialize home location autocomplete
  const initHomeLocationAutocomplete = () => {
    if (homeAutocompleteInitialized) return;
    if (!window.google?.maps?.places) {
      console.log("Google Places not loaded yet");
      return;
    }
    
    const input = document.getElementById('home-location-input');
    if (!input) return;
    
    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      types: ['(cities)'],
      fields: ['formatted_address', 'geometry', 'address_components', 'place_id']
    });
    
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place && place.geometry) {
        // Extract city and state
        let city = '';
        let state = '';
        let zipCode = '';
        
        for (const component of place.address_components || []) {
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.short_name;
          }
          if (component.types.includes('postal_code')) {
            zipCode = component.long_name;
          }
        }
        
        setHomeLocation({
          address: place.formatted_address,
          city,
          state,
          zip_code: zipCode,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        });
        setHomeLocationSearch(place.formatted_address);
        toast.success(`Location set: ${city || place.formatted_address}`);
      }
    });
    
    setHomeAutocompleteInitialized(true);
  };

  const clearHomeLocation = () => {
    setHomeLocation(null);
    setHomeLocationSearch("");
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Account deleted successfully");
      logout();
      navigate("/");
    } catch (e) {
      toast.error("Failed to delete account");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Password validation checks
  const passwordChecks = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    match: newPassword === confirmPassword && confirmPassword.length > 0
  };
  const isPasswordValid = passwordChecks.length && passwordChecks.uppercase && passwordChecks.number && passwordChecks.match;

  const handleChangePassword = async () => {
    if (!isPasswordValid) {
      toast.error("Please meet all password requirements");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully!");
      setShowChangePassword(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const addLocation = () => {
    const newId = crypto.randomUUID();
    setLocations([...locations, {
      id: newId,
      name: "",
      address: "",
      schedule: [],
      happy_hours: [],
      drinks: [],
      menu_links: []
    }]);
    // Default to search mode for new locations
    setLocationSearchMode(prev => ({ ...prev, [newId]: true }));
  };

  const updateLocation = (index, field, value) => {
    const updated = [...locations];
    updated[index][field] = value;
    setLocations(updated);
  };

  const removeLocation = (index) => {
    setLocations(locations.filter((_, i) => i !== index));
  };

  // Google Places Autocomplete
  const initPlacesAutocomplete = (inputId, locIndex, locationId) => {
    if (!window.google?.maps?.places) {
      console.log("Google Places not loaded yet");
      return;
    }
    
    const input = document.getElementById(inputId);
    if (!input) return;
    
    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      types: ['establishment'],
      fields: ['name', 'formatted_address', 'geometry', 'place_id']
    });
    
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place && place.name) {
        const updated = [...locations];
        updated[locIndex].name = place.name;
        updated[locIndex].address = place.formatted_address || "";
        updated[locIndex].place_id = place.place_id;
        if (place.geometry?.location) {
          updated[locIndex].lat = place.geometry.location.lat();
          updated[locIndex].lng = place.geometry.location.lng();
        }
        setLocations(updated);
        setSearchInputs(prev => ({ ...prev, [locationId]: place.name }));
        toast.success(`Selected: ${place.name}`);
      }
    });
  };

  const toggleLocationMode = (locationId, toSearchMode) => {
    setLocationSearchMode(prev => ({ ...prev, [locationId]: toSearchMode }));
    if (toSearchMode) {
      // Initialize autocomplete when switching to search mode
      setTimeout(() => {
        const locIndex = locations.findIndex(l => l.id === locationId);
        if (locIndex !== -1) {
          initPlacesAutocomplete(`place-search-${locationId}`, locIndex, locationId);
        }
      }, 100);
    }
  };

  const addSchedule = (locIndex) => {
    const updated = [...locations];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    updated[locIndex].schedule.push({ 
      date: tomorrow.toISOString().split('T')[0], 
      start: "18:00", 
      end: "02:00" 
    });
    setLocations(updated);
  };

  const updateSchedule = (locIndex, schedIndex, field, value) => {
    const updated = [...locations];
    updated[locIndex].schedule[schedIndex][field] = value;
    setLocations(updated);
  };

  const removeSchedule = (locIndex, schedIndex) => {
    const updated = [...locations];
    updated[locIndex].schedule = updated[locIndex].schedule.filter((_, i) => i !== schedIndex);
    setLocations(updated);
  };

  // Menu links functions
  const addMenuLink = (locIndex) => {
    const updated = [...locations];
    if (!updated[locIndex].menu_links) {
      updated[locIndex].menu_links = [];
    }
    updated[locIndex].menu_links.push({ name: "Menu", url: "" });
    setLocations(updated);
  };

  const updateMenuLink = (locIndex, linkIndex, field, value) => {
    const updated = [...locations];
    updated[locIndex].menu_links[linkIndex][field] = value;
    setLocations(updated);
  };

  const removeMenuLink = (locIndex, linkIndex) => {
    const updated = [...locations];
    updated[locIndex].menu_links = updated[locIndex].menu_links.filter((_, i) => i !== linkIndex);
    setLocations(updated);
  };

  const addHappyHour = (locIndex) => {
    const updated = [...locations];
    updated[locIndex].happy_hours.push({ day: "Monday", start: "17:00", end: "19:00", description: "" });
    setLocations(updated);
  };

  const updateHappyHour = (locIndex, hhIndex, field, value) => {
    const updated = [...locations];
    updated[locIndex].happy_hours[hhIndex][field] = value;
    setLocations(updated);
  };

  const removeHappyHour = (locIndex, hhIndex) => {
    const updated = [...locations];
    updated[locIndex].happy_hours = updated[locIndex].happy_hours.filter((_, i) => i !== hhIndex);
    setLocations(updated);
  };

  // Add signature drink
  const addSignatureDrink = (locIndex) => {
    const updated = [...locations];
    if (!updated[locIndex].signature_drinks) {
      updated[locIndex].signature_drinks = [];
    }
    updated[locIndex].signature_drinks.push({ 
      id: crypto.randomUUID(),
      name: "", 
      ingredients: "", 
      price: "" 
    });
    setLocations(updated);
  };

  const updateSignatureDrink = (locIndex, drinkIndex, field, value) => {
    const updated = [...locations];
    updated[locIndex].signature_drinks[drinkIndex][field] = value;
    setLocations(updated);
  };

  const removeSignatureDrink = (locIndex, drinkIndex) => {
    const updated = [...locations];
    updated[locIndex].signature_drinks = updated[locIndex].signature_drinks.filter((_, i) => i !== drinkIndex);
    setLocations(updated);
  };

  // Add happy hour drink
  const addHappyHourDrink = (locIndex, hhIndex) => {
    const updated = [...locations];
    if (!updated[locIndex].happy_hours[hhIndex].drinks) {
      updated[locIndex].happy_hours[hhIndex].drinks = [];
    }
    updated[locIndex].happy_hours[hhIndex].drinks.push({ 
      id: crypto.randomUUID(),
      name: "", 
      ingredients: "", 
      price: "" 
    });
    setLocations(updated);
  };

  const updateHappyHourDrink = (locIndex, hhIndex, drinkIndex, field, value) => {
    const updated = [...locations];
    updated[locIndex].happy_hours[hhIndex].drinks[drinkIndex][field] = value;
    setLocations(updated);
  };

  const removeHappyHourDrink = (locIndex, hhIndex, drinkIndex) => {
    const updated = [...locations];
    updated[locIndex].happy_hours[hhIndex].drinks = updated[locIndex].happy_hours[hhIndex].drinks.filter((_, i) => i !== drinkIndex);
    setLocations(updated);
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    return `${API}/files/${path}?auth=${token}`;
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  };

  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="edit-profile">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-white/5 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold text-white">Edit Profile</h1>
        </div>
        <Button 
          onClick={handleSave}
          disabled={loading}
          className="btn-primary"
          data-testid="save-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? "Saving..." : "Save"}
        </Button>
      </header>

      <main className="p-6 space-y-8 pb-20">
        {/* Profile Picture */}
        <div className="flex flex-col items-center">
          <div className="relative">
            <Avatar className="w-28 h-28 border-2 border-primary">
              <AvatarImage src={getImageUrl(user.profile_image)} />
              <AvatarFallback className="bg-primary/20 text-primary text-2xl">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <button
              onClick={handleImagePick}
              disabled={uploading}
              className="absolute bottom-0 right-0 p-2 rounded-full bg-primary text-black hover:bg-primary/90 transition-colors"
              data-testid="upload-photo-btn"
            >
              <Camera className="w-5 h-5" />
            </button>
            {/* File input fallback for web browsers */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
          {uploading && <p className="text-white/60 text-sm mt-2">Uploading...</p>}
        </div>

        {/* Basic Info */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">Basic Info</h2>
          <div className="space-y-2">
            <Label className="text-white/80">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-dark"
              data-testid="name-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">Bio</Label>
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell people about yourself..."
              className="input-dark min-h-[100px]"
              data-testid="bio-input"
            />
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Privacy Settings
          </h2>
          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
            <div className="flex-1">
              <p className="text-white font-medium">Require Follow Approval</p>
              <p className="text-white/50 text-sm mt-1">
                When enabled, you'll need to approve follow requests before others can follow you
              </p>
            </div>
            <Switch
              checked={requireFollowApproval}
              onCheckedChange={setRequireFollowApproval}
              data-testid="require-approval-toggle"
            />
          </div>
          <div className="p-4 rounded-lg bg-white/5">
            <p className="text-white font-medium mb-2">Who can see your followers/following</p>
            <p className="text-white/50 text-sm mb-3">
              Control who can view your followers and following lists
            </p>
            <select
              value={followersVisibility}
              onChange={(e) => setFollowersVisibility(e.target.value)}
              className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white focus:border-primary focus:outline-none"
              data-testid="followers-visibility-select"
            >
              <option value="everyone" className="bg-[#1a1a1a]">Everyone</option>
              <option value="followers" className="bg-[#1a1a1a]">Followers only</option>
              <option value="only_me" className="bg-[#1a1a1a]">Only me</option>
            </select>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Notification Settings
          </h2>
          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
            <div className="flex-1">
              <p className="text-white font-medium">Vendor Notifications</p>
              <p className="text-white/50 text-sm mt-1">
                Receive notifications from vendors you follow about specials, events, happy hours, and updates
              </p>
            </div>
            <Switch
              checked={vendorNotificationsEnabled}
              onCheckedChange={setVendorNotificationsEnabled}
              data-testid="vendor-notifications-toggle"
            />
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
            <div className="flex-1">
              <p className="text-white font-medium">PourCircle App Tips</p>
              <p className="text-white/50 text-sm mt-1">
                Receive helpful tips and updates from PourCircle about app features
              </p>
            </div>
            <Switch
              checked={appTipsEnabled}
              onCheckedChange={setAppTipsEnabled}
              data-testid="app-tips-toggle"
            />
          </div>
        </div>

        {/* Contact Admin - Bartenders Only */}
        {user?.role === "bartender" && (
          <div className="glass-card p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Contact Us
            </h2>
            <p className="text-white/60 text-sm">
              Changed jobs? Request a notification to be sent to your followers announcing your new location. 
              Have suggestions for app improvements? We'd love to hear from you!
            </p>
            <a 
              href="mailto:admin@pourcircle.net?subject=PourCircle%20Request&body=Username:%20"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors text-sm"
            >
              <Mail className="w-4 h-4" />
              Email admin@pourcircle.net
            </a>
            <p className="text-white/40 text-xs">
              Please include your username in your email.
            </p>
          </div>
        )}

        {/* Home Location */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Your Location
          </h2>
          <p className="text-white/60 text-sm">
            Set your location to discover featured bartenders near you and enable location-based search.
          </p>
          
          <div className="space-y-2">
            <Label className="text-white/80">City or Area</Label>
            <div className="relative">
              <Input
                id="home-location-input"
                type="text"
                value={homeLocationSearch}
                onChange={(e) => setHomeLocationSearch(e.target.value)}
                onFocus={initHomeLocationAutocomplete}
                placeholder="Search for your city..."
                className="input-dark"
                data-testid="home-location-input"
              />
              {homeLocation && (
                <button
                  type="button"
                  onClick={clearHomeLocation}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Current Location Display */}
            {homeLocation && (
              <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-primary text-sm font-medium">{homeLocation.address}</p>
                <p className="text-white/50 text-xs mt-1">
                  {homeLocation.city && homeLocation.state ? `${homeLocation.city}, ${homeLocation.state}` : 'Location set'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Change Password */}
        <div className="glass-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              Password
            </h2>
            <Button
              onClick={() => setShowChangePassword(!showChangePassword)}
              variant="ghost"
              className="text-primary text-sm"
              data-testid="change-password-toggle"
            >
              {showChangePassword ? "Cancel" : "Change Password"}
            </Button>
          </div>
          
          {showChangePassword && (
            <div className="space-y-4 pt-2">
              {/* Current Password */}
              <div className="space-y-2">
                <Label className="text-white/80">Current Password</Label>
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="input-dark pr-10"
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* New Password */}
              <div className="space-y-2">
                <Label className="text-white/80">New Password</Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-dark pr-10"
                    placeholder="Enter new password"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* Confirm Password */}
              <div className="space-y-2">
                <Label className="text-white/80">Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-dark"
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
              
              {/* Password Requirements */}
              <div className="p-3 rounded-lg bg-white/5 space-y-2">
                <p className="text-white/60 text-xs font-medium mb-2">Password Requirements:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className={`flex items-center gap-2 ${passwordChecks.length ? 'text-green-400' : 'text-white/40'}`}>
                    {passwordChecks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    <span>8+ characters</span>
                  </div>
                  <div className={`flex items-center gap-2 ${passwordChecks.uppercase ? 'text-green-400' : 'text-white/40'}`}>
                    {passwordChecks.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    <span>1 uppercase</span>
                  </div>
                  <div className={`flex items-center gap-2 ${passwordChecks.number ? 'text-green-400' : 'text-white/40'}`}>
                    {passwordChecks.number ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    <span>1 number</span>
                  </div>
                  <div className={`flex items-center gap-2 ${passwordChecks.match ? 'text-green-400' : 'text-white/40'}`}>
                    {passwordChecks.match ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    <span>Passwords match</span>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={handleChangePassword}
                disabled={!isPasswordValid || changingPassword || !currentPassword}
                className="w-full btn-primary"
                data-testid="submit-change-password"
              >
                {changingPassword ? "Changing..." : "Update Password"}
              </Button>
            </div>
          )}
        </div>

        {/* Bartender-specific fields */}
        {isBartender && (
          <>
            {/* Payment Links */}
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-semibold text-white">Payment Usernames</h2>
              <p className="text-white/50 text-sm">Add your usernames so customers can send tips</p>
              <div className="space-y-2">
                <Label className="text-white/80">Venmo Username</Label>
                <Input
                  value={venmoLink}
                  onChange={(e) => setVenmoLink(e.target.value)}
                  placeholder="@yourname"
                  className="input-dark"
                  data-testid="venmo-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/80">Cash App Username</Label>
                <Input
                  value={cashappLink}
                  onChange={(e) => setCashappLink(e.target.value)}
                  placeholder="$yourname"
                  className="input-dark"
                  data-testid="cashapp-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/80">PayPal Username</Label>
                <Input
                  value={paypalLink}
                  onChange={(e) => setPaypalLink(e.target.value)}
                  placeholder="yourname"
                  className="input-dark"
                  data-testid="paypal-input"
                />
              </div>
            </div>

            {/* Work Locations */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Work Locations
                </h2>
                <Button onClick={addLocation} variant="outline" className="border-primary/50 text-primary hover:bg-primary/10">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Location
                </Button>
              </div>

              {locations.map((loc, locIndex) => (
                <div key={loc.id || locIndex} className="glass-card p-6 space-y-4">
                  <div className="flex items-start justify-between">
                    <h3 className="text-white font-medium">Location {locIndex + 1}</h3>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => removeLocation(locIndex)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Location Input Mode Toggle */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-white/80">Find Location</Label>
                      <div className="flex gap-1 bg-white/5 rounded-lg p-1">
                        <button
                          type="button"
                          onClick={() => toggleLocationMode(loc.id, true)}
                          className={`px-3 py-1 text-xs rounded-md transition-colors ${
                            locationSearchMode[loc.id] !== false 
                              ? "bg-primary text-white" 
                              : "text-white/60 hover:text-white"
                          }`}
                        >
                          <Search className="w-3 h-3 inline mr-1" />
                          Search
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleLocationMode(loc.id, false)}
                          className={`px-3 py-1 text-xs rounded-md transition-colors ${
                            locationSearchMode[loc.id] === false 
                              ? "bg-primary text-white" 
                              : "text-white/60 hover:text-white"
                          }`}
                        >
                          Manual
                        </button>
                      </div>
                    </div>

                    {/* Search Mode */}
                    {locationSearchMode[loc.id] !== false && (
                      <div className="space-y-2">
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
                          <input
                            id={`place-search-${loc.id}`}
                            type="text"
                            defaultValue={searchInputs[loc.id] || loc.name || ""}
                            placeholder="Search for a bar, restaurant, or venue..."
                            className="w-full h-10 pl-10 pr-4 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-primary"
                            onFocus={() => initPlacesAutocomplete(`place-search-${loc.id}`, locIndex, loc.id)}
                          />
                        </div>
                        {loc.name && loc.address && (
                          <div className="p-3 bg-white/5 rounded-lg border border-primary/30">
                            <p className="text-white font-medium">{loc.name}</p>
                            <p className="text-white/60 text-sm">{loc.address}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Manual Mode */}
                    {locationSearchMode[loc.id] === false && (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-white/80 text-sm">Bar/Venue Name</Label>
                          <Input
                            value={loc.name}
                            onChange={(e) => updateLocation(locIndex, "name", e.target.value)}
                            placeholder="The Golden Tap"
                            className="input-dark"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/80 text-sm">Address</Label>
                          <Input
                            value={loc.address}
                            onChange={(e) => updateLocation(locIndex, "address", e.target.value)}
                            placeholder="123 Main St, City, State"
                            className="input-dark"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Schedule */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-white/80 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Schedule
                      </Label>
                      <Button onClick={() => addSchedule(locIndex)} size="sm" variant="ghost" className="text-primary text-xs">
                        <Plus className="w-3 h-3 mr-1" />
                        Add Shift
                      </Button>
                    </div>
                    {loc.schedule?.map((s, sIndex) => (
                      <div key={sIndex} className="space-y-2 p-3 bg-white/5 rounded-lg">
                        {/* Date Picker */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal border-white/10 bg-white/5 text-white hover:bg-white/10"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                              {s.date ? format(new Date(s.date + 'T00:00:00'), "EEEE, MMMM do") : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-background border-white/10" align="start">
                            <Calendar
                              mode="single"
                              selected={s.date ? new Date(s.date + 'T00:00:00') : undefined}
                              onSelect={(date) => {
                                if (date) {
                                  updateSchedule(locIndex, sIndex, "date", format(date, "yyyy-MM-dd"));
                                }
                              }}
                              initialFocus
                              className="rounded-md"
                            />
                          </PopoverContent>
                        </Popover>
                        
                        {/* Time Selectors */}
                        <div className="flex gap-2 items-center">
                          <div className="flex-1 relative">
                            <select
                              value={s.start}
                              onChange={(e) => updateSchedule(locIndex, sIndex, "start", e.target.value)}
                              className="w-full input-dark h-10 px-3 rounded-lg appearance-none cursor-pointer"
                            >
                              {timeOptions.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                          </div>
                          <span className="text-white/40">to</span>
                          <div className="flex-1 relative">
                            <select
                              value={s.end}
                              onChange={(e) => updateSchedule(locIndex, sIndex, "end", e.target.value)}
                              className="w-full input-dark h-10 px-3 rounded-lg appearance-none cursor-pointer"
                            >
                              {timeOptions.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeSchedule(locIndex, sIndex)}
                            className="text-white/40 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Menu Links */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-white/80 flex items-center gap-2">
                        <LinkIcon className="w-4 h-4" />
                        Menu Links
                      </Label>
                      <Button onClick={() => addMenuLink(locIndex)} size="sm" variant="ghost" className="text-primary text-xs">
                        <Plus className="w-3 h-3 mr-1" />
                        Add Menu
                      </Button>
                    </div>
                    {loc.menu_links?.map((link, linkIndex) => (
                      <div key={linkIndex} className="flex gap-2 items-center">
                        <select
                          value={link.name === "Custom" || !["Menu", "Food Menu", "Drink Menu", "Wine List", "Beer List", "Cocktails", "Specials", "Lunch", "Dinner", "Happy Hour"].includes(link.name) ? "Custom" : link.name}
                          onChange={(e) => {
                            if (e.target.value === "Custom") {
                              updateMenuLink(locIndex, linkIndex, "name", "");
                            } else {
                              updateMenuLink(locIndex, linkIndex, "name", e.target.value);
                            }
                          }}
                          className="w-28 input-dark h-10 px-3 rounded-lg"
                        >
                          <option value="Menu">Menu</option>
                          <option value="Food Menu">Food Menu</option>
                          <option value="Drink Menu">Drink Menu</option>
                          <option value="Wine List">Wine List</option>
                          <option value="Beer List">Beer List</option>
                          <option value="Cocktails">Cocktails</option>
                          <option value="Specials">Specials</option>
                          <option value="Lunch">Lunch</option>
                          <option value="Dinner">Dinner</option>
                          <option value="Happy Hour">Happy Hour</option>
                          <option value="Custom">Custom</option>
                        </select>
                        {(link.name === "" || !["Menu", "Food Menu", "Drink Menu", "Wine List", "Beer List", "Cocktails", "Specials", "Lunch", "Dinner", "Happy Hour"].includes(link.name)) && (
                          <Input
                            type="text"
                            value={link.name}
                            onChange={(e) => updateMenuLink(locIndex, linkIndex, "name", e.target.value)}
                            placeholder="Custom label"
                            className="w-28 input-dark"
                          />
                        )}
                        <Input
                          type="url"
                          value={link.url}
                          onChange={(e) => updateMenuLink(locIndex, linkIndex, "url", e.target.value)}
                          placeholder="https://restaurant.com/menu"
                          className="flex-1 input-dark"
                        />
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeMenuLink(locIndex, linkIndex)}
                          className="text-white/40 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Happy Hours */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-secondary flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Happy Hours
                      </Label>
                      <Button onClick={() => addHappyHour(locIndex)} size="sm" variant="ghost" className="text-secondary text-xs">
                        <Plus className="w-3 h-3 mr-1" />
                        Add Happy Hour
                      </Button>
                    </div>
                    {loc.happy_hours?.map((hh, hhIndex) => (
                      <div key={hhIndex} className="p-3 rounded-lg bg-secondary/10 border border-secondary/20 space-y-2">
                        <div className="flex gap-2 items-center">
                          <select
                            value={hh.day}
                            onChange={(e) => updateHappyHour(locIndex, hhIndex, "day", e.target.value)}
                            className="flex-1 input-dark h-10 px-3 rounded-lg"
                          >
                            {days.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                          <Input
                            type="time"
                            value={hh.start}
                            onChange={(e) => updateHappyHour(locIndex, hhIndex, "start", e.target.value)}
                            className="input-dark w-24"
                          />
                          <span className="text-white/40">to</span>
                          <Input
                            type="time"
                            value={hh.end}
                            onChange={(e) => updateHappyHour(locIndex, hhIndex, "end", e.target.value)}
                            className="input-dark w-24"
                          />
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeHappyHour(locIndex, hhIndex)}
                            className="text-white/40 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Input
                          value={hh.description}
                          onChange={(e) => updateHappyHour(locIndex, hhIndex, "description", e.target.value)}
                          placeholder="General description (e.g., Half off all wells)"
                          className="input-dark"
                        />
                        
                        {/* Happy Hour Drinks */}
                        <div className="mt-3 pt-3 border-t border-secondary/20">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white/60 text-xs font-accent">Happy Hour Drinks</span>
                            <Button 
                              onClick={() => addHappyHourDrink(locIndex, hhIndex)} 
                              size="sm" 
                              variant="ghost" 
                              className="text-secondary text-xs h-6 px-2"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Drink
                            </Button>
                          </div>
                          {hh.drinks?.map((drink, drinkIndex) => (
                            <div key={drink.id || drinkIndex} className="p-2 rounded-lg bg-black/20 mb-2 space-y-2">
                              <div className="flex gap-2 items-center">
                                <Input
                                  value={drink.name}
                                  onChange={(e) => updateHappyHourDrink(locIndex, hhIndex, drinkIndex, "name", e.target.value)}
                                  placeholder="Drink name"
                                  className="input-dark flex-1 h-9 text-sm"
                                />
                                <Input
                                  value={drink.price}
                                  onChange={(e) => updateHappyHourDrink(locIndex, hhIndex, drinkIndex, "price", e.target.value)}
                                  placeholder="$5"
                                  className="input-dark w-20 h-9 text-sm"
                                />
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => removeHappyHourDrink(locIndex, hhIndex, drinkIndex)}
                                  className="text-white/40 hover:text-red-400 h-9 w-9"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                              <Input
                                value={drink.ingredients}
                                onChange={(e) => updateHappyHourDrink(locIndex, hhIndex, drinkIndex, "ingredients", e.target.value)}
                                placeholder="Ingredients (e.g., Tequila, lime, triple sec)"
                                className="input-dark h-9 text-sm"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Signature Drinks */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-primary flex items-center gap-2">
                        <GlassWater className="w-4 h-4" />
                        Signature Drinks
                      </Label>
                      <Button 
                        onClick={() => addSignatureDrink(locIndex)} 
                        size="sm" 
                        variant="ghost" 
                        className="text-primary text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Drink
                      </Button>
                    </div>
                    {loc.signature_drinks?.map((drink, drinkIndex) => (
                      <div key={drink.id || drinkIndex} className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
                        <div className="flex gap-2 items-center">
                          <Input
                            value={drink.name}
                            onChange={(e) => updateSignatureDrink(locIndex, drinkIndex, "name", e.target.value)}
                            placeholder="Drink name"
                            className="input-dark flex-1"
                          />
                          <Input
                            value={drink.price}
                            onChange={(e) => updateSignatureDrink(locIndex, drinkIndex, "price", e.target.value)}
                            placeholder="$12"
                            className="input-dark w-24"
                          />
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeSignatureDrink(locIndex, drinkIndex)}
                            className="text-white/40 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Input
                          value={drink.ingredients}
                          onChange={(e) => updateSignatureDrink(locIndex, drinkIndex, "ingredients", e.target.value)}
                          placeholder="Ingredients (e.g., Bourbon, bitters, orange peel, sugar)"
                          className="input-dark"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Blocked Users */}
        <Button 
          onClick={() => navigate("/followers")}
          variant="outline"
          className="w-full border-white/20 text-white/70 hover:bg-white/5"
          data-testid="blocked-users-btn"
        >
          <Shield className="w-4 h-4 mr-2" />
          Manage Blocked Users
        </Button>

        {/* Legal & Policies */}
        <div className="glass-card p-6 space-y-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Legal & Policies
          </h2>
          <Link 
            to="/privacy" 
            className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="privacy-policy-link"
          >
            <ScrollText className="w-5 h-5 text-white/60" />
            <span className="text-white/80">Privacy Policy</span>
          </Link>
          <Link 
            to="/terms" 
            className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="terms-of-use-link"
          >
            <FileText className="w-5 h-5 text-white/60" />
            <span className="text-white/80">Terms of Use</span>
          </Link>
          <Link 
            to="/safety" 
            className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="safety-standards-link"
          >
            <ShieldCheck className="w-5 h-5 text-white/60" />
            <span className="text-white/80">Safety Standards</span>
          </Link>
        </div>

        {/* Logout */}
        <Button 
          onClick={handleLogout}
          variant="outline"
          className="w-full border-red-400/50 text-red-400 hover:bg-red-400/10"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Log Out
        </Button>

        {/* Delete Account */}
        <Button 
          onClick={() => setShowDeleteConfirm(true)}
          variant="outline"
          className="w-full border-red-600/50 text-red-500 hover:bg-red-600/10"
          data-testid="delete-account-btn"
        >
          <UserX className="w-4 h-4 mr-2" />
          Delete Account
        </Button>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent className="bg-[#0a0a0a] border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete your account?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/60">
                This action cannot be undone. This will permanently delete your account, 
                all your messages, followers, and remove your profile from PourCircle.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 text-white border-white/10 hover:bg-white/10">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {deleting ? "Deleting..." : "Yes, delete my account"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* AI Profile Assistant - Bartenders Only */}
        {isBartender && (
          <AIProfileAssistant 
            onProfileUpdate={(updatedProfile) => {
              // Update local state with AI changes
              if (updatedProfile.name) setName(updatedProfile.name);
              if (updatedProfile.bio) setBio(updatedProfile.bio);
              if (updatedProfile.work_locations) setLocations(updatedProfile.work_locations);
              if (updatedProfile.venmo_link) setVenmoLink(updatedProfile.venmo_link);
              if (updatedProfile.cashapp_link) setCashappLink(updatedProfile.cashapp_link);
              if (updatedProfile.paypal_link) setPaypalLink(updatedProfile.paypal_link);
              // Update auth context
              updateUser(updatedProfile);
            }}
          />
        )}
      </main>
    </div>
  );
};

export default EditProfile;
